import {FunctionComponent, useState} from "react";
import {useRegionsQuery} from "../../../../api/consultants/api";
import {useTranslation} from "react-i18next";

interface Props {
  onRegionChange: (regionId: number | undefined, regionName: string | undefined) => void;
  onSearchChange: (search: string) => void;
}

const ConsultantFilters: FunctionComponent<Props> = ({onRegionChange, onSearchChange}) => {
  const {t} = useTranslation();
  const {data: regions} = useRegionsQuery();
  const [searchValue, setSearchValue] = useState('');

  const slovakRegions = regions?.filter(r => r.country === 'sk') ?? [];
  const czechRegions = regions?.filter(r => r.country === 'cz') ?? [];
  const otherRegions = regions?.filter(r => r.country !== 'sk' && r.country !== 'cz') ?? [];

  const handleRegionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === '') {
      onRegionChange(undefined, undefined);
    } else {
      const regionId = parseInt(value);
      const region = regions?.find(r => r.id === regionId);
      onRegionChange(regionId, region?.name);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearchChange(searchValue);
  };

  return (
    <div className="flex flex-col md:flex-row gap-4 mb-6">
      <div className="flex-1">
        <label htmlFor="region-filter" className="block text-sm font-medium text-gray-700 mb-1">
          {t('consultantFinder.filterByRegion')}
        </label>
        <select
          id="region-filter"
          onChange={handleRegionChange}
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">{t('consultantFinder.allRegions')}</option>
          {slovakRegions.length > 0 && (
            <optgroup label={t('consultantFinder.slovakRegions')}>
              {slovakRegions.map(region => (
                <option key={region.id} value={region.id}>{region.name}</option>
              ))}
            </optgroup>
          )}
          {czechRegions.length > 0 && (
            <optgroup label={t('consultantFinder.czechRegions')}>
              {czechRegions.map(region => (
                <option key={region.id} value={region.id}>{region.name}</option>
              ))}
            </optgroup>
          )}
          {otherRegions.map(region => (
            <option key={region.id} value={region.id}>{region.name}</option>
          ))}
        </select>
      </div>
      <div className="flex-1">
        <label htmlFor="search-filter" className="block text-sm font-medium text-gray-700 mb-1">
          {t('consultantFinder.searchLabel')}
        </label>
        <form onSubmit={handleSearchSubmit} className="flex gap-2">
          <input
            id="search-filter"
            type="text"
            value={searchValue}
            onChange={handleSearchChange}
            placeholder={t('consultantFinder.searchPlaceholder')}
            className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            type="submit"
            className="bg-blue-500 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-md transition-colors"
          >
            {t('consultantFinder.search')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ConsultantFilters;
